import { Component } from '@theme/component';
import { fetchConfig, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';
import { addToCart } from '@theme/cart-queue';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;
const ERROR_DURATION = 3000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  /** @type {number | undefined} */
  #errorTimeout;

  /** @type {string | null} */
  #labelBeforeError = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    if (this.#errorTimeout) clearTimeout(this.#errorTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    this.animateAddToCart();
    // No fly-to-cart animation (owner decision, 2026-10-07): the header
    // bubble updating at once is the feedback.
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }

  /**
   * Shows a failed add on the button itself for a few seconds ("Sold out" or
   * the generic error): on a product card the theme's error text has no
   * place, so the customer saw the button flash "Added" and nothing else.
   * @param {string} message - Shopify's error description.
   */
  showError(message) {
    const button = this.refs.addToCartButton;
    const label = button?.querySelector('.add-to-cart-text__content');
    if (!button || !label) return;

    if (this.#errorTimeout) clearTimeout(this.#errorTimeout);
    this.#labelBeforeError ??= label.textContent;
    button.classList.remove('atc-added');
    button.classList.add('atc-error');
    label.textContent = /sold out/i.test(message ?? '')
      ? Theme.translations.sold_out
      : Theme.translations.add_to_cart_error;

    this.#errorTimeout = setTimeout(() => {
      label.textContent = this.#labelBeforeError;
      this.#labelBeforeError = null;
      button.classList.remove('atc-error');
    }, ERROR_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

// Add to cart without the browser's own form submission. A click on a submit
// button makes the browser bring style and layout of the whole page up to
// date before it fires `submit`, inside the click: ~30-45 ms on a collection
// with 200 cards, and again on the next frame once the stepper is in place
// (CLIENT-155). Our product forms never submit natively anyway
// (ProductFormComponent.handleSubmit cancels it), so the click is cancelled
// and the same `submit` event is dispatched straight to the form: every
// submit listener still runs, without the forced update. Only for forms
// inside <product-form-component>, so a form without our handler keeps the
// browser's submission. Clicks already handled (card stepper on a product in
// the cart) are left alone.
document.addEventListener('click', (event) => {
  if (event.defaultPrevented) return;
  const button =
    event.target instanceof Element ? event.target.closest('button[type="submit"][ref="addToCartButton"]') : null;
  const form = button instanceof HTMLButtonElement ? button.form : null;
  if (!button || button.disabled || !form || !form.closest('product-form-component')) return;
  event.preventDefault();
  form.dispatchEvent(new SubmitEvent('submit', { bubbles: true, cancelable: true, submitter: button }));
});

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
/** Longest an Add to cart waits for a variant switch to come back from the server (ms). */
const VARIANT_WAIT = 4000;

class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /**
   * While a variant switch waits for the server (see #onVariantSelected): the
   * variant shown optimistically, and a promise that settles when the
   * server's update arrives (or after VARIANT_WAIT).
   * @type {{variantId: string, settled: Promise<void>, resolve: () => void} | null}
   */
  #variantSwitch = null;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    // Add to cart clicked during a variant switch: the button already acts for
    // the new variant, but its line item properties (configuration pickers)
    // follow with the server's update. Add once that is in.
    if (this.#variantSwitch) {
      this.#variantSwitch.settled.then(() => this.handleSubmit(event));
      return;
    }

    if (this.#timeout) clearTimeout(this.#timeout);

    // Check if the add to cart button is disabled and do an early return if it is
    if (this.refs.addToCartButtonContainer?.refs.addToCartButton?.getAttribute('disabled') === 'true') return;

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    const formData = new FormData(form);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    // Through the cart queue (assets/cart-queue.js): adds clicked in quick
    // succession go to Shopify together instead of racing each other.
    const item = cartItemFromForm(formData);
    const fetchCfg = fetchConfig('javascript', { body: formData });
    const request = item
      ? addToCart([item], cartItemComponentsSectionIds)
      : fetch(Theme.routes.cart_add_url, {
          ...fetchCfg,
          headers: {
            ...fetchCfg.headers,
            Accept: 'text/html',
          },
        }).then(async (response) => ({ data: await response.json(), dispatch: true }));

    request
      .then(({ data: response, dispatch }) => {
        if (response.status) {
          window.dispatchEvent(new CartErrorEvent(this.id, response.message));
          // Not when Shopify added part of the quantity ("Only 80 items were
          // added..."): that is not a failed add, the text error says it.
          if (!/were added|was added/i.test(response.description ?? '')) {
            this.refs.addToCartButtonContainer?.showError?.(response.description || response.message);
          }

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, 10000);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, 5000);
          }


          // One event per request: when several clicks went out together,
          // the last of them announces the change (with the sections).
          if (dispatch) {
            this.dispatchEvent(
              new CartAddEvent({}, id.toString(), {
                source: 'product-form-component',
                itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
                productId: this.dataset.productId,
                sections: response.sections,
              })
            );
          }


          // ADD WARRANTY TO CART
          const warrantyInput = form.querySelector('input[name="warranty-add-to-cart"]:checked');

          // Skip option ("Skip" card) has an empty value — treat as no warranty
          if (!warrantyInput || !warrantyInput.value) return;

          const warrantyId = Number(warrantyInput.value);
          if (!warrantyId) return;
          // Only after the product itself is in the cart, so a sold-out
          // product never leaves its warranty behind alone. One warranty per
          // product added (the quantity stepper adds several at once).
          const warrantyQuantity = Math.max(1, Number(formData.get('quantity')) || 1);
          return addToCart(
            [{ id: warrantyId, quantity: warrantyQuantity, properties: { main_variant_id: String(id) } }],
            cartItemComponentsSectionIds
          ).then(({ ok, data: json, dispatch: announce }) => {
            if (!ok) {
              console.warn('warranty add failed', json);
              window.dispatchEvent(new CartErrorEvent(this.id, json?.message ?? 'Cart Error'));
              return;
            }
            if (!announce) return;
            this.dispatchEvent(
              new CartAddEvent(json, String(warrantyId), {
                source: 'product-form-component',
                itemCount: warrantyQuantity,
                productId: this.dataset.productId,
                sections: json.sections,
              })
            );
          });
          // }
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        // add more thing to do in here if needed.
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {

    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const BIS_trigger = document.querySelector('#BIS_trigger');

    const { variantId, addToCartButtonContainer } = this.refs;

    // Shown optimistically already: keep the warranty choice the customer may
    // have made since (renderWarranties would reset it).
    const shownOptimistically = this.#variantSwitch?.variantId === String(event.detail.resource?.id ?? '');
    this.#variantSwitch?.resolve();
    this.#variantSwitch = null;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    if (!currentAddToCartButton) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      addToCartButtonContainer.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      BIS_trigger?.classList.add('visible');
    } else {
      addToCartButtonContainer.enable();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      BIS_trigger?.classList.remove('visible');
    }

    // Update the add to cart button text and icon
    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    // Update the variant ID
    variantId.value = event.detail.resource.id ?? '';

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }

    if (shownOptimistically) return;

    if (Object.entries(window.warranties?.[event.detail.resource.id] ?? {}).length > 0) {
      this.renderWarranties(event.detail.resource.id);
    } else {
      const warrantyWrapper = this.querySelector('[data-warranty-wrapper]');
      if (warrantyWrapper) {
        warrantyWrapper.classList.add('hidden');
      }
    }
  };

 renderWarranties(variantId) {
    const warranties = window.warranties[variantId];
    const warrantyWrapper = this.querySelector('[data-warranty-wrapper]');
    const warrantyOptions = this.querySelector('[data-warranty-options]');
    if (!warrantyWrapper || !warrantyOptions) return;

    // Sort warranties by numeric price ascending (price is formatted as "$ 299.00" — strip to compare)
    const parsePrice = (str) => Number(String(str).replace(/[^0-9.]/g, '')) || 0;
    const parseYears = (str) => {
      const n = parseInt(String(str), 10);
      return n > 0 ? n : 1;
    };
    const sorted = Object.entries(warranties).sort(
      (a, b) => parsePrice(a[1].price) - parsePrice(b[1].price)
    );
    const cheapestPrice = sorted.length ? parsePrice(sorted[0][1].price) : 0;
    const cheapestYears = sorted.length ? parseYears(sorted[0][1].duration) : 1;

    // Labor Day 2026: window.freeWarranties (emitted by blocks/buy-buttons.liquid)
    // maps a printer variant id -> the warranty variant id that is free with it.
    // When this variant has one, that card renders at $0, badged, and pre-selected.
    const freeMap = window.freeWarranties || {};
    const freeWarrantyKey = freeMap[String(variantId)] ? String(freeMap[String(variantId)]) : '';
    // Mirrors Liquid's `money_without_trailing_zeros` — drops `.00` but keeps `.50`.
    const formatMoneyShort = (amount) => {
      const hasCents = Math.round(amount * 100) % 100 !== 0;
      return '$' + amount.toLocaleString('en-US', {
        minimumFractionDigits: hasCents ? 2 : 0,
        maximumFractionDigits: 2,
      });
    };

    warrantyOptions.replaceChildren();

    // Stable, page-unique prefix derived from the parent product. Prevents
    // duplicate-ID collisions when multiple product-form-components mount on
    // the same page (e.g. quick view + main PDP).
    const idPrefix = this.dataset.productId || variantId;

    const createOption = (modifier, inputAttrs, labelText, priceText, isDefault) => {
      const label = document.createElement('label');
      label.className = 'warranty-option' + (modifier ? ' ' + modifier : '') + (isDefault ? ' is-selected' : '');
      if (modifier === 'warranty-option--skip') label.dataset.warrantySkip = '';

      const input = document.createElement('input');
      input.type = 'radio';
      input.name = 'warranty-add-to-cart';
      input.id = inputAttrs.id;
      input.value = inputAttrs.value;
      if (inputAttrs.years !== undefined) input.dataset.warrantyYears = String(inputAttrs.years);
      if (inputAttrs.durationLabel !== undefined) input.dataset.warrantyDurationLabel = inputAttrs.durationLabel;
      if (isDefault) input.checked = true;
      label.appendChild(input);

      if (inputAttrs.saveText) {
        const saveEl = document.createElement('span');
        saveEl.className = 'warranty-option__save-tag';
        saveEl.textContent = inputAttrs.saveText;
        label.appendChild(saveEl);
      }

      const labelEl = document.createElement('span');
      labelEl.className = 'warranty-option__label';
      labelEl.textContent = labelText;
      label.appendChild(labelEl);

      const priceEl = document.createElement('span');
      const isFreeCard = !!inputAttrs.isFree;
      priceEl.className = 'warranty-option__price' + ((modifier === 'warranty-option--skip' || isFreeCard) ? ' warranty-option__price--free' : '');
      priceEl.textContent = priceText;
      label.appendChild(priceEl);
      // The struck-through original price is a SIBLING of the price span, not a
      // child: the block's order-summary JS reads .warranty-option__price text
      // and strips non-digits, so "$0 $139" inside it would parse as 139.
      if (isFreeCard && inputAttrs.wasText) {
        const was = document.createElement('s');
        was.className = 'warranty-option__was';
        was.textContent = inputAttrs.wasText;
        label.appendChild(was);
      }

      return label;
    };

    // Skip option always first. NOT default-checked — the longest warranty is
    // pre-selected below to match the Liquid render and boost attach rate.
    warrantyOptions.appendChild(
      createOption(
        'warranty-option--skip',
        { id: `warranty-skip-${idPrefix}`, value: '' },
        'Skip',
        '$0',
        false
      )
    );

    sorted.forEach(([key, value], index) => {
      const years = parseYears(value.duration);
      const price = parsePrice(value.price);
      const baseline = (cheapestPrice * years) / cheapestYears;
      const save = baseline - price;
      const isFree = freeWarrantyKey !== '' && String(key) === freeWarrantyKey;
      // A free warranty always wins the default; otherwise keep the existing
      // "longest is pre-selected" behaviour.
      const isDefault = freeWarrantyKey !== '' ? isFree : index === sorted.length - 1;

      warrantyOptions.appendChild(
        createOption(
          isFree ? 'warranty-option--labor-day' : '',
          {
            id: `warranty-${key}`,
            value: String(key),
            years,
            durationLabel: value.duration || '',
            saveText: isFree ? 'Labor Day' : (save > 0 ? `Save ${formatMoneyShort(save)}` : ''),
            isFree,
            wasText: isFree ? formatMoneyShort(price) : '',
          },
          `+${value.duration || value.title || ''}`,
          isFree ? '$0' : `+${value.price || ''}`,
          isDefault
        )
      );
    });

    // Fire a change event so the warranty-block JS recalculates the order summary
    // + coverage label using the newly-rendered default card.
    const defaultInput = warrantyOptions.querySelector('input[name="warranty-add-to-cart"]:checked');
    if (defaultInput) {
      defaultInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (warrantyWrapper.classList.contains('hidden')) {
      warrantyWrapper.classList.remove('hidden');
    }
  }

  /**
   * The button's label at once: "Out of stock" for a sold-out variant, the
   * Add to cart label otherwise (both on the component, from
   * snippets/add-to-cart-button.liquid). It used to wait for the server's
   * update (0.6-0.9 s), showing a greyed "Add to cart" or an active "Out of
   * stock" meanwhile; the update morphs the button to the same text.
   * @param {HTMLElement | undefined} container
   * @param {boolean} available
   */
  #setButtonLabel(container, available) {
    const label = container?.querySelector('.add-to-cart-text__content');
    const text = available ? container?.dataset.labelAdd : container?.dataset.labelSoldOut;
    if (label && text) label.textContent = text;
    const icon = container?.querySelector('.add-to-cart-text .add-to-cart-icon');
    if (icon instanceof HTMLElement) icon.style.display = available ? '' : 'none';
  }

  /**
   * A variant was picked; the server's update (#onVariantUpdate) follows.
   *
   * The theme disabled Add to cart until then, which showed as a loading
   * button on every switch. The picked option already carries its variant id
   * and availability (snippets/variant-main-picker.liquid), so the form
   * switches at once: variant id, button state, warranty options (built from
   * window.warranties on the page). Price, stock and the rest still follow
   * with the update; an Add to cart clicked before it waits for it
   * (handleSubmit).
   *
   * Falls back to the theme's disabled button when the variant is not known
   * up front: another switch still on its way on a product with several
   * options (the other options' variant ids on the page are then out of
   * date), or a switch to another product.
   * Accelerated checkout is also disabled via its own event listener not exposed to the theme.
   * @param {Event & {detail?: {resource?: {id?: string}}}} event
   */
  #onVariantSelected = (event) => {
    const container = this.refs.addToCartButtonContainer;
    const picker = event.target instanceof Element ? event.target : null;
    const optionId = event.detail?.resource?.id;
    /** @type {HTMLElement | null} */
    const option =
      picker && optionId ? picker.querySelector(`[data-option-value-id="${CSS.escape(String(optionId))}"]`) : null;
    const variantId = option?.dataset.variantId ?? '';
    const otherProduct = option?.dataset.connectedProductUrl;
    // With a single option, every option's variant id stays right whatever
    // was picked before; with more, they are only right for the selection
    // the page was rendered with.
    const singleOption = (picker?.querySelectorAll('fieldset, select').length ?? 0) <= 1;
    const known = Boolean(variantId) && !otherProduct && (!this.#variantSwitch || singleOption);

    this.#variantSwitch?.resolve();
    /** @type {() => void} */
    let resolve = () => {};
    const settled = new Promise((done) => {
      resolve = done;
    });
    const current = { variantId: known ? variantId : '', settled, resolve };
    this.#variantSwitch = current;
    // Never hold an Add to cart for long if the update does not come.
    setTimeout(() => {
      if (this.#variantSwitch === current) this.#variantSwitch = null;
      resolve();
    }, VARIANT_WAIT);

    if (!known) {
      container?.disable();
      return;
    }

    this.refs.variantId.value = variantId;
    const BIS_trigger = document.querySelector('#BIS_trigger');
    const available = option?.dataset.optionAvailable !== 'false';
    if (!available) {
      container?.disable();
      BIS_trigger?.classList.add('visible');
    } else {
      container?.enable();
      BIS_trigger?.classList.remove('visible');
    }
    this.#setButtonLabel(container, available);

    if (Object.entries(window.warranties?.[variantId] ?? {}).length > 0) {
      this.renderWarranties(variantId);
    } else {
      this.querySelector('[data-warranty-wrapper]')?.classList.add('hidden');
    }
  };
}



/**
 * The product form as a /cart/add.js item, so it can be sent together with
 * other adds. Null when the form carries something JSON cannot (a file).
 * @param {FormData} formData
 */
function cartItemFromForm(formData) {
  const id = Number(formData.get('id'));
  if (!id) return null;
  /** @type {{id: number, quantity: number, properties?: Record<string, string>, selling_plan?: number}} */
  const item = { id, quantity: Number(formData.get('quantity')) || 1 };
  const sellingPlan = Number(formData.get('selling_plan'));
  if (sellingPlan) item.selling_plan = sellingPlan;
  /** @type {Record<string, string>} */
  const properties = {};
  for (const [key, value] of formData) {
    const match = key.match(/^properties\[(.+)\]$/);
    if (!match) continue;
    if (typeof value !== 'string') return null;
    properties[match[1]] = value;
  }
  if (Object.keys(properties).length) item.properties = properties;
  return item;
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    // Web Animations instead of a requestAnimationFrame loop: a transform
    // animation runs on the compositor, so it starts with the next frame and
    // stays smooth while the main thread is busy with the cart response
    // (drawer and card re-renders). The rAF loop moved the circle only on
    // frames the page had time for.
    const duration = 600;
    const steps = 24;
    /** @type {Keyframe[]} */
    const keyframes = [];
    for (let i = 0; i <= steps; i++) {
      const progress = i / steps;
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);
      // Scale down as it approaches the cart
      keyframes.push({
        transform: `translate(${position.x}px, ${position.y}px) scale(${1 - progress * 0.5})`,
        opacity: 1,
      });
    }

    const flight = this.animate(keyframes, { duration, easing: 'linear', fill: 'forwards' });
    flight.finished
      .then(() =>
        // Fade out the flying thingy
        this.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 300, fill: 'forwards' }).finished
      )
      .catch(() => {})
      .finally(() => this.remove());
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
